// customerService.js - Customer data handling and formatting

export class CustomerService {
    constructor(component) {
        this.component = component;
    }

    async loadCustomerDetails(customerId, { page = 1, perPage = 25 } = {}) {
        try {
            const params = new URLSearchParams({ page, per_page: perPage })
            const token = localStorage.getItem('token')
            const apiBase = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '')
            const response = await fetch(`${apiBase}/api/v1/kunden/${customerId}?${params}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                customer: data.customer,
                contacts: data.contacts || [],
                statistics: data.statistics || null,
                orders: data.orders || [],
                meta: data.meta || {}
            };
        } catch (error) {
            console.error('Error loading customer details:', error);
            throw error;
        }
    }

    async createCustomer(customerData) {
        try {
            const response = await fetch('/api/customers', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ customer: customerData })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error creating customer:', error);
            throw error;
        }
    }

    async updateCustomer(customerId, customerData) {
        const data = await this._request(`/api/v1/kunden/${customerId}`, {
            method: 'PUT',
            body: JSON.stringify({ customer: customerData })
        });
        return data.customer;
    }

    async createContact(customerId, contactData) {
        const data = await this._request(`/api/v1/kunden/${customerId}/contacts`, {
            method: 'POST',
            body: JSON.stringify({ contact: contactData })
        });
        return data.contact;
    }

    async updateContact(contactId, contactData) {
        const data = await this._request(`/api/v1/contacts/${contactId}`, {
            method: 'PUT',
            body: JSON.stringify({ contact: contactData })
        });
        return data.contact;
    }

    async deleteContact(contactId) {
        await this._request(`/api/v1/contacts/${contactId}`, { method: 'DELETE' });
        return true;
    }

    // Shared fetch helper: prefixes the API base, attaches the JWT, and surfaces
    // the server's German error message on a non-2xx response.
    async _request(path, options = {}) {
        const apiBase = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '');
        const token = localStorage.getItem('token');
        const response = await fetch(`${apiBase}${path}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...(options.headers || {})
            }
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const msg = (data.errors && data.errors.join(', ')) || data.error || `HTTP ${response.status}`;
            throw new Error(msg);
        }
        return data;
    }

    async deleteCustomer(customerId) {
        try {
            const response = await fetch(`/api/customers/${customerId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return true;
        } catch (error) {
            console.error('Error deleting customer:', error);
            throw error;
        }
    }

    async loadCustomers(params = {}) {
        try {
            const queryParams = new URLSearchParams();

            // Add pagination parameters
            if (params.currentPage) queryParams.append('page', params.currentPage);
            if (params.perPage) queryParams.append('per_page', params.perPage);

            // Add sorting parameters
            if (params.sortField) {
                queryParams.append('sort_field', params.sortField);
                queryParams.append('sort_direction', params.sortDirection);
            }

            // Add filter parameters
            if (params.filters) {
                Object.keys(params.filters).forEach(key => {
                    if (params.filters[key]) {
                        queryParams.append(key, params.filters[key]);
                    }
                });
            }

            const response = await fetch(`/api/customers?${queryParams.toString()}`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                customers: data.data,
                meta: data.meta
            };
        } catch (error) {
            console.error('Error loading customers:', error);
            throw error;
        }
    }
}

export class CustomerFormatter {
    static getCustomerStatusClass(status) {
        const statusClasses = {
            'active': 'customer-status-active',
            'inactive': 'customer-status-inactive',
            'pending': 'customer-status-pending'
        };

        return statusClasses[status] || 'customer-status-default';
    }

    static getCustomerStatusText(status) {
        const statusTexts = {
            'active': 'Aktiv',
            'inactive': 'Inaktiv',
            'pending': 'Ausstehend'
        };

        return statusTexts[status] || 'Unbekannt';
    }

    static formatCurrency(value) {
        if (value === null || value === undefined || value === '') return '-';

        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(value);
    }

    static formatPercentage(value) {
        if (value === null || value === undefined || value === '') return '-';

        return `${value}%`;
    }

    static formatDate(dateString) {
        if (!dateString) return '-';

        try {
            return new Date(dateString).toLocaleDateString('de-DE');
        } catch (error) {
            return '-';
        }
    }

    static formatPhoneNumber(phoneNumber) {
        if (!phoneNumber) return '-';

        // Basic phone number formatting for German numbers
        const cleaned = phoneNumber.replace(/\D/g, '');

        if (cleaned.startsWith('49')) {
            // German international format
            return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5)}`;
        } else if (cleaned.startsWith('0')) {
            // German national format
            return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
        }

        return phoneNumber;
    }

    static getPaymentTermsText(term) {
        const paymentTerms = {
            'sofort': 'Sofort',
            '7_tage': '7 Tage',
            '14_tage': '14 Tage',
            '30_tage': '30 Tage',
            '60_tage': '60 Tage',
            '90_tage': '90 Tage'
        };

        return paymentTerms[term] || term || '-';
    }

    static formatAddress(customer) {
        const parts = [];

        if (customer.k_str) parts.push(customer.k_str);

        const cityParts = [];
        if (customer.k_plz) cityParts.push(customer.k_plz);
        if (customer.k_ort) cityParts.push(customer.k_ort);
        if (cityParts.length > 0) parts.push(cityParts.join(' '));

        if (customer.k_land && customer.k_land !== 'Deutschland') {
            parts.push(customer.k_land);
        }

        return parts.join(', ') || '-';
    }
}

export const CUSTOMER_CONSTANTS = {
    TABLE_COLUMNS: [
        { key: 'k_nr', label: 'Kundennr.', sortable: true },
        { key: 'k_name', label: 'Firmenname', sortable: true },
        { key: 'k_ort', label: 'Ort', sortable: true },
        { key: 'k_tel', label: 'Telefon', sortable: false },
        { key: 'k_email', label: 'E-Mail', sortable: false },
        { key: 'k_status', label: 'Status', sortable: true },
        { key: 'total_orders', label: 'Aufträge', sortable: true },
        { key: 'last_order_date', label: 'Letzter Auftrag', sortable: true, type: 'date' },
        { key: 'actions', label: 'Aktionen', sortable: false }
    ],

    STATUS_OPTIONS: [
        { value: 'active', text: 'Aktiv' },
        { value: 'inactive', text: 'Inaktiv' },
        { value: 'pending', text: 'Ausstehend' }
    ],

    PAYMENT_TERMS: [
        { value: 'sofort', text: 'Sofort' },
        { value: '7_tage', text: '7 Tage' },
        { value: '14_tage', text: '14 Tage' },
        { value: '30_tage', text: '30 Tage' },
        { value: '60_tage', text: '60 Tage' },
        { value: '90_tage', text: '90 Tage' }
    ],

    COUNTRIES: [
        { value: 'Deutschland', text: 'Deutschland' },
        { value: 'Österreich', text: 'Österreich' },
        { value: 'Schweiz', text: 'Schweiz' },
        { value: 'Frankreich', text: 'Frankreich' },
        { value: 'Niederlande', text: 'Niederlande' },
        { value: 'Belgien', text: 'Belgien' },
        { value: 'Italien', text: 'Italien' },
        { value: 'Polen', text: 'Polen' },
        { value: 'Tschechien', text: 'Tschechien' },
        { value: 'Dänemark', text: 'Dänemark' }
    ]
};